/**
 * Links to the access-denied page.
 *
 * The page the user asked for travels as `next`, so that after an
 * organization switch the user lands on it again. `reason` says why access
 * was refused when the app knows it (the platform-admin route guard); a
 * server 403 carries no reason. Every `next` value is validated with the
 * shared safeReturnPath from @agion/auth, on the way in and on the way out.
 */
import { safeReturnPath, type Organization } from '@agion/auth'

export const ACCESS_DENIED_PATH = '/access-denied'

export type AccessDeniedReason = 'platform-admin'

const REASONS: readonly AccessDeniedReason[] = ['platform-admin']

function safeNext(raw: string): string {
  const next = safeReturnPath(raw)
  const pathname = (next.split(/[?#]/, 1)[0] ?? '').replace(/\/+$/, '')
  return pathname === ACCESS_DENIED_PATH ? '/' : next
}

/** Address of the access-denied page for a refused request of `requested`. */
export function accessDeniedPath(requested: string, reason?: AccessDeniedReason): string {
  const params = new URLSearchParams()
  const next = safeNext(requested)
  if (next !== '/') params.set('next', next)
  if (reason) params.set('reason', reason)
  const query = params.toString()
  return query ? `${ACCESS_DENIED_PATH}?${query}` : ACCESS_DENIED_PATH
}

/** Reads `next` (always a safe same-site path, '/' by default) and `reason`. */
export function readAccessDeniedParams(search: string): { next: string; reason: AccessDeniedReason | null } {
  const params = new URLSearchParams(search)
  const rawReason = params.get('reason')
  const reason = REASONS.find(value => value === rawReason) ?? null
  return { next: safeNext(params.get('next') ?? '/'), reason }
}

/**
 * The organization to offer as a one-click switch after a platform refusal
 * (agion/Agion#2310): the first one, in the server's list order, that grants
 * platform administration. Null when the current organization already grants
 * it, when the current organization is unknown, or when no organization is
 * marked, which includes an older server that does not send the mark.
 */
export function platformOrganizationToOffer(
  organizations: readonly Organization[],
  currentOrganizationId: string | undefined,
): Organization | null {
  if (!currentOrganizationId) return null
  const current = organizations.find(org => org.id === currentOrganizationId)
  if (current?.canAdministerPlatform === true) return null
  return organizations.find(org => org.canAdministerPlatform === true) ?? null
}
